import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { 
  Settings, 
  Bell, 
  User, 
  Shield, 
  Database,
  Mail,
  Globe,
  Moon,
  Sun,
  Save,
  ChevronRight
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: '', label: 'No filter' },
  { value: 'coo', label: 'COO / Chief Operating Officer' },
  { value: 'compliance', label: 'Compliance Officer' },
  { value: 'regulatory', label: 'Regulatory Affairs' },
  { value: 'billing', label: 'Billing / Revenue Cycle' },
  { value: 'clinical', label: 'Clinical Operations' },
  { value: 'quality', label: 'Quality Assurance' },
  { value: 'legal', label: 'Legal / Risk Management' },
];

export function SettingsPage() {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [selectedRole, setSelectedRole] = useState(
    localStorage.getItem('userRole') || ''
  );

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    localStorage.setItem('userRole', value);
    // Notify listeners (dashboard) to refetch/re-filter
    window.dispatchEvent(new Event('roleChanged'));
  };

  return (
    <div className="flex-1 p-6 space-y-6 overflow-auto bg-background">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Role selection for dashboard filtering */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-sky-100 rounded-lg">
            <Settings className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Role-based filtering</h2>
            <p className="text-sm text-muted-foreground">
              Select your role to tailor dashboard regulatory updates.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <Label htmlFor="role-select">Your role</Label>
          <select
            id="role-select"
            value={selectedRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            We match your role against each change&apos;s audience (healthcare roles, departments, facilities).
          </p>
        </div>
      </Card>

      {/* Notification Settings */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Notification Preferences</h2>
            <p className="text-sm text-muted-foreground">
              Choose how you want to be notified about regulatory changes
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">
                Receive alerts via email for critical updates
              </p>
            </div>
            <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-muted-foreground">
                Get instant notifications in the browser
              </p>
            </div>
            <Switch checked={pushNotifications} onCheckedChange={setPushNotifications} />
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div>
              <p className="font-medium">Weekly Digest</p>
              <p className="text-sm text-muted-foreground">
                Receive a summary of all updates every Monday
              </p>
            </div>
            <Switch checked={weeklyDigest} onCheckedChange={setWeeklyDigest} />
          </div>
        </div>
      </Card>

      {/* Profile Settings */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <User className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Profile Information</h2>
            <p className="text-sm text-muted-foreground">
              Update your personal and organization details
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName">First Name</Label>
            <Input id="firstName" defaultValue="Sarah" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="lastName">Last Name</Label>
            <Input id="lastName" defaultValue="Johnson" className="mt-2" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="email">Email Address</Label>
            <Input id="email" type="email" defaultValue="sarah.johnson@infervia.com" className="mt-2" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="organization">Organization</Label>
            <Input id="organization" defaultValue="Infervia Healthcare" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="role">Role</Label>
            <Input id="role" defaultValue="Compliance Officer" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="department">Department</Label>
            <Input id="department" defaultValue="Compliance" className="mt-2" />
          </div>
        </div>

        <Button className="mt-6">
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </Card>

      {/* System Preferences */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <Globe className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">System Preferences</h2>
            <p className="text-sm text-muted-foreground">
              Customize your dashboard experience
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center space-x-3">
              {darkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  Enable dark theme for the interface
                </p>
              </div>
            </div>
            <Switch checked={darkMode} onCheckedChange={setDarkMode} />
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Database className="w-5 h-5" />
              <div>
                <p className="font-medium">Auto-Sync</p>
                <p className="text-sm text-muted-foreground">
                  Automatically sync with data sources every 2 hours
                </p>
              </div>
            </div>
            <Switch checked={autoSync} onCheckedChange={setAutoSync} />
          </div>
        </div>
      </Card>

      {/* Security Settings */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Security & Privacy</h2>
            <p className="text-sm text-muted-foreground">
              Manage your account security settings
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <button className="w-full flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors">
            <div className="text-left">
              <p className="font-medium">Change Password</p>
              <p className="text-sm text-muted-foreground">
                Update your account password
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          <button className="w-full flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors">
            <div className="text-left">
              <p className="font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security
              </p>
            </div>
            <Badge variant="outline">Not Enabled</Badge>
          </button>

          <button className="w-full flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors">
            <div className="text-left">
              <p className="font-medium">Active Sessions</p>
              <p className="text-sm text-muted-foreground">
                Manage your logged-in devices
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
      </Card>

      {/* Data & Storage */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Database className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Data & Storage</h2>
            <p className="text-sm text-muted-foreground">
              Manage your data and storage preferences
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium">Storage Used</p>
              <p className="text-sm text-muted-foreground">2.4 GB / 10 GB</p>
            </div>
            <div className="w-full bg-secondary rounded-full h-2">
              <div className="bg-primary h-2 rounded-full" style={{ width: '24%' }}></div>
            </div>
          </div>

          <button className="w-full flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors">
            <div className="text-left">
              <p className="font-medium">Download My Data</p>
              <p className="text-sm text-muted-foreground">
                Export all your data and reports
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          <button className="w-full flex items-center justify-between p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-200">
            <div className="text-left">
              <p className="font-medium text-red-600">Delete Account</p>
              <p className="text-sm text-red-600/70">
                Permanently delete your account and data
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-red-600" />
          </button>
        </div>
      </Card>
    </div>
  );
}
